import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { stringFormat, T, uploadFile } from 'src/shared/helpers';
import CloudUploadIcon from 'src/shared/material_icons/file/cloud_upload.svg';
import FolderOpenIcon from 'src/shared/material_icons/file/folder_open.svg';
import PaperPlaneIcon from 'src/shared/material_icons/content/send.svg';
import PenIcon from 'src/shared/material_icons/image/edit.svg';
import PlusIcon from 'src/shared/material_icons/content/add.svg';
import DiceIcon from 'src/shared/material_icons/places/casino.svg';
import QuestionCircleIcon from 'src/shared/material_icons/action/help.svg';
import StopIcon from 'src/shared/material_icons/av/stop.svg';
import ThGridIcon from 'src/shared/material_icons/file/grid_view.svg';
import TimesIcon from 'src/shared/material_icons/navigation/close.svg';
import { AiPromptSendButton } from 'src/components/widgets/ai';
import { AiCreditsNotice } from 'src/components/shared/aiAssistant/AiCreditsNotice';
import styled from 'styled-components';
import { useDropdown } from '../../../../widgets/hooks/useDropdown';
import { Icon } from '../../../../widgets/icon';
import { showErrorNotification } from '../../../../widgets/notificationToaster';
import {
    MAX_3D_IMAGE_DIMENSION,
    MAX_3D_UPLOAD_SIZE_BYTES,
    MAX_3D_UPLOAD_SIZE_MB,
    MAX_UPLOAD_SIZE_BYTES,
    MAX_UPLOAD_SIZE_MB,
    PROMPT_DISABLED_PLACEHOLDER,
    PROMPT_MAX_LENGTH,
    TEXT_TO_3D_RANDOM_PROMPTS,
    TOOLS
} from '../../constants';
import { fadeIn, spin } from '../../styles/animations';
import { InputImageData, PresetSelectionState, ToolType } from '../../types';
import { getImageDimensions } from '../../utils/imageHelpers';
import { MultiViewDropZone, MultiViewInputData, ViewSlotId } from '../inputs/multiViewDropZone';
import { PromptInput } from '../inputs/promptInput';
import { PresetDropdown, PresetDropdownValue } from '../presets/presetDropdown';
import { SelectedPresetInfo } from '../presets/presetOptionButtons';
import { getPresetTypesForTool } from '../presets/presetTypeConfig';
import { HiddenInput } from '../shared/sharedStyles';

const PanelContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px 32px 32px;
    background: transparent;

    @media (max-height: 720px) {
        padding: 12px 24px 20px;
    }

    @media (max-height: 660px) {
        padding: 8px 20px 14px;
    }
`;

const ControlsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    border: 1px solid rgb(255, 255, 255);
    border-radius: 28px;
    background: rgba(255, 255, 255, 0.45);
    width: 900px;
    max-width: 100%;

    @media (max-height: 720px) {
        gap: 12px;
        padding: 12px 14px;
    }

    @media (max-height: 660px) {
        gap: 10px;
        padding: 10px 12px;
    }
`;

const InputRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const MultiViewControls = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
`;

const MultiViewActionRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const PromptInputWrapper = styled.div`
    flex: 1;
    width: 100%;
    display: flex;
    align-items: center;
    gap: 16px;
`;

const UploadZoneIcon = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;

    ${Icon} {
        width: 20px;
        height: 20px;
        fill: currentColor;
    }
`;

const UploadZoneText = styled.div`
    text-align: center;
`;

const UploadZoneTitle = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${(props) => props.theme.app.textColor};
    margin-bottom: 1px;
`;

const UploadZoneHint = styled.div`
    font-size: 11px;
    color: #6b7280;
`;

// Split upload container with upload and catalog zones
const SplitUploadContainer = styled.div`
    flex: 1;
    display: flex;
    align-items: stretch;
    gap: 8px;
`;

const UploadZoneHalf = styled.button<{ $isDragOver?: boolean }>`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    border: 2px dashed ${(props) => (props.$isDragOver ? '#3b82f6' : props.theme.app.borderColor)};
    border-radius: 12px;
    background: ${(props) => (props.$isDragOver ? 'rgba(59, 130, 246, 0.1)' : 'transparent')};
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: #3b82f6;
        background: rgba(59, 130, 246, 0.05);
    }
`;

const OrSeparator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 500;
    color: #9ca3af;
    padding: 0;
    flex-shrink: 0;
`;

const CatalogZone = styled.button`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 16px;
    border: 2px dashed #c4b5fd;
    border-radius: 12px;
    background: rgba(124, 58, 237, 0.03);
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        border-color: #7c3aed;
        background: rgba(124, 58, 237, 0.08);
    }
`;

const CatalogZoneIcon = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 8px;
    background: linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #ec4899 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;

    ${Icon} {
        width: 20px;
        height: 20px;
        fill: currentColor;
    }
`;

const CatalogZoneTitle = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: #7c3aed;
    margin-bottom: 1px;
`;

const CatalogZoneHint = styled.div`
    font-size: 11px;
    color: #a78bfa;
`;

// Container for input images and prompt
const InputWithPreviews = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const InputPreviews = styled.div`
    display: flex;
    gap: 8px;
    flex-shrink: 0;
`;

const InputPreviewItem = styled.div`
    position: relative;
    min-height: 58px;
    min-width: 58px;
    max-width: 58px;
    max-height: 58px;
`;

const InputPreviewImage = styled.div`
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
    border: 1px solid ${(props) => props.theme.app.borderColor};
    background: ${(props) => props.theme.app.backgroundColor};

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
`;

const InputPreviewRemove = styled.button`
    position: absolute;
    top: 0px;
    right: 0px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: none;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;

    &:hover {
        background: #dc2626;
    }

    ${Icon} {
        width: 8px;
        height: 8px;
        fill: currentColor;
    }
`;

const AddMoreButton = styled.button`
    width: 58px;
    height: 58px;
    border-radius: 50%;
    border: 2px dashed ${(props) => props.theme.app.borderColor};
    background: transparent;
    color: #9ca3af;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;

    &:hover {
        border-color: #3b82f6;
        color: #3b82f6;
    }

    ${Icon} {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

const AddMoreMenu = styled.div`
    min-width: 200px;
    padding: 6px;
`;

const AddMoreMenuItem = styled.button<{ $disabled?: boolean }>`
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: ${(props) => (props.$disabled ? '#c0c0c0' : props.theme.app.textColor)};
    font-size: 13px;
    font-weight: 500;
    cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
    transition: background 0.15s ease;
    text-align: left;

    &:hover {
        background: ${(props) => (props.$disabled ? 'transparent' : '#f3f4f6')};
    }

    ${Icon} {
        width: 16px;
        height: 16px;
        fill: currentColor;
        flex-shrink: 0;
    }
`;

const InputUploadSpinner = styled.div`
    width: 20px;
    height: 20px;
    border: 2px solid #e5e7eb;
    border-top-color: #3b82f6;
    border-radius: 50%;
    animation: ${spin} 0.8s linear infinite;
`;

const AutoCaptureSpinner = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
    border-radius: 50%;

    &::after {
        content: '';
        width: 20px;
        height: 20px;
        border: 2px solid #e5e7eb;
        border-top-color: #7c3aed;
        border-radius: 50%;
        animation: ${spin} 0.8s linear infinite;
    }
`;

const ClickablePreviewItem = styled(InputPreviewItem)`
    cursor: pointer;

    &:hover ${InputPreviewImage} {
        border-color: #7c3aed;
    }
`;

const PreviewEditingOverlay = styled.div`
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    animation: ${fadeIn} 0.2s ease;
    z-index: 1;

    svg {
        width: 16px;
        height: 16px;
        fill: #7c3aed;
    }
`;

const GenerateButton = styled(AiPromptSendButton)`
    > * {
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

const PresetSelectorsRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
`;

const PresetDropdownsGroup = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
`;

const RandomPromptButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 8px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: #7c3aed;
    font-size: 11px;
    cursor: pointer;

    &:hover {
        background: rgba(124, 58, 237, 0.08);
    }

    ${Icon} {
        width: 16px;
        height: 16px;
        fill: currentColor;
    }
`;

const HelpLink = styled.a`
    &:hover {
        color: #6d28d9;
        text-decoration: underline;
    }

    ${Icon} {
        width: 24px;
        height: 24px;
        fill: currentColor;
    }
`;

// Types re-exported from central types.ts for backwards compatibility
export type { AdOptions, InputImageData, PresetSelectionState, VideoOptions } from '../../types';

interface BottomPanelProps {
    toolId: ToolType;
    prompt: string;
    onPromptChange: (value: string) => void;
    /** Selection state */
    selectionState: PresetSelectionState;
    /** Called when a preset is selected for a given type */
    onPresetSelect: (typeId: string, value: SelectedPresetInfo | null) => void;
    onGenerate: () => void;
    /** Called when the user clicks stop during generation */
    onStop?: () => void;
    isGenerating: boolean;
    canGenerate: boolean;
    /** The preset type ID to hide from dropdowns (managed by upload zone or product context) */
    inputPresetType?: string;
    /** Input images uploaded by the user */
    inputImages: InputImageData[];
    /** Called when input images change */
    onInputImagesChange: (images: InputImageData[]) => void;
    /** View-tagged inputs used by Multi-View to 3D. */
    multiViewInputs?: MultiViewInputData[];
    onMultiViewInputsChange?: (inputs: MultiViewInputData[]) => void;
    supportedMultiViewSlotIds?: readonly ViewSlotId[];
    /** Whether in product mode (images come from product context, not upload zone) */
    isProductMode?: boolean;
    /** Link to the guide/help documentation for this tool */
    guideLink?: string;
    /** Callback to open the product catalog dialog */
    onOpenCatalog?: () => void;
    /** Callback to open the asset library */
    onOpenLibrary?: () => void;
    /** Whether the product iframe is currently active */
    isIframeMode?: boolean;
    /** Placeholder text for the prompt input */
    promptPlaceholder?: string;
    /** Callback to toggle the input editor in the canvas area */
    onToggleInputEditor?: () => void;
    /** Whether a background 3D auto-capture is in progress */
    isAutoCapturing?: boolean;
    /** Whether the user is currently editing the product input (2D/3D selector) */
    isProductInputEditing?: boolean;
    /** Callback to remove the product input (shows warning, exits product mode) */
    onRemoveProduct?: () => void;
}

export const BottomPanel: FC<BottomPanelProps> = ({
    toolId,
    prompt,
    onPromptChange,
    selectionState,
    onPresetSelect,
    onGenerate,
    onStop,
    isGenerating,
    canGenerate,
    inputPresetType,
    inputImages,
    onInputImagesChange,
    multiViewInputs = [],
    onMultiViewInputsChange,
    supportedMultiViewSlotIds,
    isProductMode = false,
    guideLink,
    onOpenCatalog,
    onOpenLibrary,
    isIframeMode = false,
    promptPlaceholder,
    onToggleInputEditor,
    isAutoCapturing = false,
    isProductInputEditing = false,
    onRemoveProduct
}) => {
    const { selections } = selectionState;
    const inputFileRef = useRef<HTMLInputElement>(null);
    const [isUploadingInput, setIsUploadingInput] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isMultiViewLoading, setIsMultiViewLoading] = useState(false);
    const [setDropdownOpen, setDropdownClose, isDropdownOpen, AddMoreDropdown] = useDropdown();

    // Check if tool supports multiple input images
    const supportsMultipleInputs = toolId === 'multi-image-fusion';

    // Check if tool supports prompt input and image input
    const toolConfig = TOOLS.find((t) => t.id === toolId);
    const allowPrompt = toolConfig?.allowPrompt !== false;
    const allowImageInput = toolConfig?.allowImageInput !== false;

    // Max 5 images for multi-image fusion
    const maxInputImages = 5;
    const canAddMoreImages = supportsMultipleInputs && inputImages.length < maxInputImages;

    // Get preset types for this tool (excluding the input type)
    const presetTypes = useMemo(() => {
        const allTypes = getPresetTypesForTool(toolId);
        return inputPresetType ? allTypes.filter((type) => type.id !== inputPresetType) : allTypes;
    }, [toolId, inputPresetType]);

    // Presets are locked until the user uploads an image or selects a product
    const presetsLocked = !isProductMode && !isIframeMode && inputImages.length === 0;
    const presetsLockedTooltip = T._('Upload your product image or select a product from your Zakeke first', 'Admin');

    // Shared upload handler: uploads files and merges into input images
    const processUploadedFiles = useCallback(
        async (files: File[], resetFileInput = false) => {
            if (files.length === 0) return;

            const isImageTo3D = toolId === 'image-to-3d';
            const maxSizeBytes = isImageTo3D ? MAX_3D_UPLOAD_SIZE_BYTES : MAX_UPLOAD_SIZE_BYTES;
            const maxSizeMB = isImageTo3D ? MAX_3D_UPLOAD_SIZE_MB : MAX_UPLOAD_SIZE_MB;
            const oversized = files.find((f) => f.size > maxSizeBytes);
            if (oversized) {
                showErrorNotification({ message: T._(`File size must not exceed ${maxSizeMB} MB.`, 'Admin') });
                if (resetFileInput && inputFileRef.current) inputFileRef.current.value = '';
                return;
            }

            if (isImageTo3D) {
                let dimensions: Array<{ width: number; height: number }>;
                try {
                    dimensions = await Promise.all(files.map(getImageDimensions));
                } catch {
                    showErrorNotification({ message: T._('Unable to read image. Please try a different file.', 'Admin') });
                    if (resetFileInput && inputFileRef.current) inputFileRef.current.value = '';
                    return;
                }
                if (dimensions.some(({ width, height }) => width > MAX_3D_IMAGE_DIMENSION || height > MAX_3D_IMAGE_DIMENSION)) {
                    showErrorNotification({
                        message: T._(`Image dimensions must not exceed ${MAX_3D_IMAGE_DIMENSION}×${MAX_3D_IMAGE_DIMENSION} pixels.`, 'Admin')
                    });
                    if (resetFileInput && inputFileRef.current) inputFileRef.current.value = '';
                    return;
                }
            }

            setIsUploadingInput(true);
            try {
                const uploadPromises = files.map(async (file) => {
                    const url = await uploadFile(file);
                    const id = `input-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
                    return { id, url, name: file.name };
                });

                const uploadedResults = await Promise.all(uploadPromises);

                if (supportsMultipleInputs) {
                    onInputImagesChange([...inputImages, ...uploadedResults]);
                } else {
                    onInputImagesChange(uploadedResults.slice(0, 1));
                }
            } catch (error) {
                console.error('Input upload failed:', error);
            } finally {
                setIsUploadingInput(false);
                if (resetFileInput && inputFileRef.current) {
                    inputFileRef.current.value = '';
                }
            }
        },
        [supportsMultipleInputs, inputImages, onInputImagesChange, toolId]
    );

    // Drag and drop handlers
    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(
        async (e: React.DragEvent) => {
            e.preventDefault();
            setIsDragOver(false);
            const imageFiles = Array.from(e.dataTransfer.files).filter((file) => file.type.startsWith('image/'));
            await processUploadedFiles(imageFiles);
        },
        [processUploadedFiles]
    );

    const handleInputFileChange = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files;
            if (!files || files.length === 0) return;
            await processUploadedFiles(Array.from(files), true);
        },
        [processUploadedFiles]
    );

    // Remove input image
    const handleRemoveInputImage = useCallback(
        (imageId: string) => {
            onInputImagesChange(inputImages.filter((img) => img.id !== imageId));
        },
        [inputImages, onInputImagesChange]
    );

    // Handle preset dropdown selection
    const handlePresetDropdownChange = useCallback(
        (typeId: string, value: PresetDropdownValue | null) => {
            if (!value) {
                onPresetSelect(typeId, null);
                return;
            }
            onPresetSelect(typeId, {
                id: value.id,
                thumbnailUrl: value.thumbnailUrl,
                factor: value.factor
            });
        },
        [onPresetSelect]
    );

    // Get selected value for a preset type
    const getSelectedValue = useCallback(
        (typeId: string): PresetDropdownValue | null => {
            const selection = selections[typeId];
            if (!selection) return null;
            if (Array.isArray(selection)) {
                // For multi-select, return first item (dropdowns don't support multi-select in this design)
                return selection[0] || null;
            }
            return {
                id: selection.id,
                thumbnailUrl: selection.thumbnailUrl,
                factor: selection.factor
            };
        },
        [selections]
    );

    // Generate / Stop action button
    const actionButton = isGenerating ? (
        <GenerateButton $isGenerating onClick={onStop} title={T._('Stop', 'Admin')}>
            <Icon>
                <StopIcon />
            </Icon>
        </GenerateButton>
    ) : (
        <GenerateButton $isGenerating={false} onClick={onGenerate} disabled={!canGenerate} title={T._('Generate', 'Admin')}>
            <Icon>
                <PaperPlaneIcon />
            </Icon>
        </GenerateButton>
    );

    const defaultPlaceholder = allowPrompt ? T._(promptPlaceholder || PROMPT_DISABLED_PLACEHOLDER, 'Admin') : T._(PROMPT_DISABLED_PLACEHOLDER, 'Admin');

    // Render the input row content based on mode
    const renderInputRow = () => {
        if (toolId === 'multi-views-to-3d') {
            return (
                <MultiViewControls>
                    <MultiViewDropZone
                        values={multiViewInputs}
                        onChange={(values) => onMultiViewInputsChange?.(values)}
                        slotIds={supportedMultiViewSlotIds}
                        onLoadingChange={setIsMultiViewLoading}
                    />
                    <MultiViewActionRow>
                        <PromptInputWrapper>
                            <PromptInput value='' onChange={() => {}} placeholder={T._(PROMPT_DISABLED_PLACEHOLDER, 'Admin')} disabled />
                        </PromptInputWrapper>
                        {isGenerating ? (
                            <GenerateButton $isGenerating onClick={onStop} title={T._('Stop', 'Admin')}>
                                <Icon>
                                    <StopIcon />
                                </Icon>
                            </GenerateButton>
                        ) : (
                            <GenerateButton
                                $isGenerating={false}
                                onClick={onGenerate}
                                disabled={!canGenerate || isMultiViewLoading}
                                title={T._('Generate', 'Admin')}
                            >
                                <Icon>
                                    <PaperPlaneIcon />
                                </Icon>
                            </GenerateButton>
                        )}
                    </MultiViewActionRow>
                </MultiViewControls>
            );
        }

        // Prompt-only mode (no image input allowed): just show prompt + action
        if (!allowImageInput) {
            return (
                <>
                    <PromptInputWrapper>
                        <PromptInput value={prompt} onChange={onPromptChange} placeholder={defaultPlaceholder} maxLength={500} disabled={isGenerating} />
                    </PromptInputWrapper>
                    {actionButton}
                </>
            );
        }

        // Product mode: show input thumbnails (if available) + prompt
        if (isProductMode) {
            if (inputImages.length > 0 || isAutoCapturing) {
                return (
                    <>
                        <InputWithPreviews>
                            <InputPreviews>
                                {isAutoCapturing && (
                                    <InputPreviewItem>
                                        <InputPreviewImage>
                                            <AutoCaptureSpinner />
                                        </InputPreviewImage>
                                    </InputPreviewItem>
                                )}
                                {inputImages.map((img) =>
                                    img.fromProduct ? (
                                        <ClickablePreviewItem key={img.id} onClick={onToggleInputEditor}>
                                            <InputPreviewImage>
                                                <img src={img.url} alt={img.name || T._('Input', 'Admin')} />
                                            </InputPreviewImage>
                                            {isProductInputEditing && (
                                                <PreviewEditingOverlay>
                                                    <PenIcon />
                                                </PreviewEditingOverlay>
                                            )}
                                            {onRemoveProduct && (
                                                <InputPreviewRemove
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        onRemoveProduct();
                                                    }}
                                                    title={T._('Remove', 'Admin')}
                                                >
                                                    <Icon>
                                                        <TimesIcon />
                                                    </Icon>
                                                </InputPreviewRemove>
                                            )}
                                        </ClickablePreviewItem>
                                    ) : (
                                        <InputPreviewItem key={img.id}>
                                            <InputPreviewImage>
                                                <img src={img.url} alt={img.name || T._('Input', 'Admin')} />
                                            </InputPreviewImage>
                                            <InputPreviewRemove onClick={() => handleRemoveInputImage(img.id)} title={T._('Remove', 'Admin')}>
                                                <Icon>
                                                    <TimesIcon />
                                                </Icon>
                                            </InputPreviewRemove>
                                        </InputPreviewItem>
                                    )
                                )}
                                {canAddMoreImages && (
                                    <AddMoreButton
                                        onClick={(e) => (isDropdownOpen ? setDropdownClose() : setDropdownOpen(e.currentTarget, 'top', 'left'))}
                                        title={T._('Add more', 'Admin')}
                                    >
                                        {isUploadingInput ? (
                                            <InputUploadSpinner />
                                        ) : (
                                            <Icon>
                                                <PlusIcon />
                                            </Icon>
                                        )}
                                    </AddMoreButton>
                                )}
                            </InputPreviews>
                            <PromptInputWrapper>
                                <PromptInput
                                    value={prompt}
                                    onChange={onPromptChange}
                                    placeholder={defaultPlaceholder}
                                    maxLength={500}
                                    disabled={isGenerating || !allowPrompt}
                                />
                                {actionButton}
                            </PromptInputWrapper>
                        </InputWithPreviews>
                        {supportsMultipleInputs && <HiddenInput ref={inputFileRef} type='file' accept='image/*' multiple onChange={handleInputFileChange} />}
                    </>
                );
            }
            // No images yet and not capturing: just prompt (no generate button until something is loaded)
            return (
                <PromptInputWrapper>
                    <PromptInput
                        value={prompt}
                        onChange={onPromptChange}
                        placeholder={defaultPlaceholder}
                        maxLength={500}
                        disabled={isGenerating || !allowPrompt}
                    />
                </PromptInputWrapper>
            );
        }

        // Iframe mode (no images): show prompt + action button
        if (isIframeMode && inputImages.length === 0) {
            return (
                <>
                    <PromptInputWrapper>
                        <PromptInput
                            value={prompt}
                            onChange={onPromptChange}
                            placeholder={defaultPlaceholder}
                            maxLength={500}
                            disabled={isGenerating || !allowPrompt}
                        />
                    </PromptInputWrapper>
                    {actionButton}
                </>
            );
        }

        // No images: show upload zone
        if (inputImages.length === 0) {
            return (
                <>
                    <SplitUploadContainer>
                        <UploadZoneHalf
                            onClick={() => inputFileRef.current?.click()}
                            disabled={isUploadingInput}
                            $isDragOver={isDragOver}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                        >
                            {isUploadingInput ? (
                                <InputUploadSpinner />
                            ) : (
                                <>
                                    <UploadZoneIcon>
                                        <Icon>
                                            <CloudUploadIcon />
                                        </Icon>
                                    </UploadZoneIcon>
                                    <UploadZoneText>
                                        <UploadZoneTitle>
                                            {supportsMultipleInputs
                                                ? T._('Drop images or click to upload', 'Admin')
                                                : T._('Drop image or click to upload', 'Admin')}
                                        </UploadZoneTitle>
                                        <UploadZoneHint>
                                            {T._(`PNG, JPG, WEBP up to ${toolId === 'image-to-3d' ? MAX_3D_UPLOAD_SIZE_MB : MAX_UPLOAD_SIZE_MB}MB`, 'Admin')}
                                        </UploadZoneHint>
                                    </UploadZoneText>
                                </>
                            )}
                        </UploadZoneHalf>

                        <OrSeparator>{T._('or', 'Admin')}</OrSeparator>

                        <CatalogZone onClick={onOpenCatalog} type='button'>
                            <CatalogZoneIcon>
                                <Icon>
                                    <ThGridIcon />
                                </Icon>
                            </CatalogZoneIcon>
                            <UploadZoneText>
                                <CatalogZoneTitle>{T._('Start with a Zakeke product', 'Admin')}</CatalogZoneTitle>
                                <CatalogZoneHint>{T._('customize or position it before AI generation', 'Admin')}</CatalogZoneHint>
                            </UploadZoneText>
                        </CatalogZone>

                        <OrSeparator>{T._('or', 'Admin')}</OrSeparator>

                        <CatalogZone onClick={onOpenLibrary} type='button'>
                            <CatalogZoneIcon>
                                <Icon>
                                    <FolderOpenIcon />
                                </Icon>
                            </CatalogZoneIcon>
                            <UploadZoneText>
                                <CatalogZoneTitle>{T._('Select from library', 'Admin')}</CatalogZoneTitle>
                                <CatalogZoneHint>{T._('Choose from your saved assets', 'Admin')}</CatalogZoneHint>
                            </UploadZoneText>
                        </CatalogZone>
                    </SplitUploadContainer>
                    <HiddenInput ref={inputFileRef} type='file' accept='image/*' multiple={supportsMultipleInputs} onChange={handleInputFileChange} />
                </>
            );
        }

        // Has images: previews + prompt + action button
        return (
            <>
                <InputWithPreviews>
                    <InputPreviews>
                        {inputImages.map((img) => (
                            <InputPreviewItem key={img.id}>
                                <InputPreviewImage>
                                    <img src={img.url} alt={img.name || T._('Input', 'Admin')} />
                                </InputPreviewImage>
                                <InputPreviewRemove onClick={() => handleRemoveInputImage(img.id)} title={T._('Remove', 'Admin')}>
                                    <Icon>
                                        <TimesIcon />
                                    </Icon>
                                </InputPreviewRemove>
                            </InputPreviewItem>
                        ))}
                        {canAddMoreImages && (
                            <AddMoreButton
                                onClick={(e) => (isDropdownOpen ? setDropdownClose() : setDropdownOpen(e.currentTarget, 'top', 'left'))}
                                title={T._('Add more', 'Admin')}
                            >
                                {isUploadingInput ? (
                                    <InputUploadSpinner />
                                ) : (
                                    <Icon>
                                        <PlusIcon />
                                    </Icon>
                                )}
                            </AddMoreButton>
                        )}
                    </InputPreviews>
                    <PromptInputWrapper>
                        <PromptInput
                            value={prompt}
                            onChange={onPromptChange}
                            placeholder={defaultPlaceholder}
                            maxLength={PROMPT_MAX_LENGTH}
                            disabled={isGenerating || !allowPrompt}
                        />
                        {actionButton}
                    </PromptInputWrapper>
                </InputWithPreviews>
                <HiddenInput ref={inputFileRef} type='file' accept='image/*' multiple={supportsMultipleInputs} onChange={handleInputFileChange} />
            </>
        );
    };

    return (
        <>
            <PanelContainer>
                <ControlsContainer>
                    <InputRow>{renderInputRow()}</InputRow>

                    {(presetTypes.length > 0 || guideLink || toolId === 'text-to-3d') && (
                        <PresetSelectorsRow>
                            <PresetDropdownsGroup>
                                {toolId === 'text-to-3d' && (
                                    <RandomPromptButton
                                        type='button'
                                        onClick={() => onPromptChange(TEXT_TO_3D_RANDOM_PROMPTS[Math.floor(Math.random() * TEXT_TO_3D_RANDOM_PROMPTS.length)])}
                                    >
                                        <Icon>
                                            <DiceIcon />
                                        </Icon>
                                        {T._('Try random prompt', 'Admin')}
                                    </RandomPromptButton>
                                )}
                                {presetTypes.map((typeConfig) => (
                                    <PresetDropdown
                                        key={typeConfig.id}
                                        toolId={toolId}
                                        typeConfig={typeConfig}
                                        selectedValue={getSelectedValue(typeConfig.id)}
                                        onSelect={(value) => handlePresetDropdownChange(typeConfig.id, value)}
                                        disabled={isGenerating || presetsLocked}
                                        disabledTooltip={presetsLocked ? presetsLockedTooltip : undefined}
                                    />
                                ))}
                            </PresetDropdownsGroup>
                            {guideLink && (
                                <HelpLink href={guideLink} target='_blank' rel='noopener noreferrer' title={T._('Help & Guide', 'Admin')}>
                                    <Icon>
                                        <QuestionCircleIcon />
                                    </Icon>
                                </HelpLink>
                            )}
                        </PresetSelectorsRow>
                    )}

                    <AiCreditsNotice />
                </ControlsContainer>
            </PanelContainer>

            {isDropdownOpen && (
                <AddMoreDropdown>
                    <AddMoreMenu>
                        <AddMoreMenuItem
                            onClick={() => {
                                setDropdownClose();
                                inputFileRef.current?.click();
                            }}
                        >
                            <Icon>
                                <CloudUploadIcon />
                            </Icon>
                            {T._('Upload image', 'Admin')}
                        </AddMoreMenuItem>
                        <AddMoreMenuItem
                            onClick={() => {
                                setDropdownClose();
                                onOpenLibrary?.();
                            }}
                        >
                            <Icon>
                                <FolderOpenIcon />
                            </Icon>
                            {T._('Select from library', 'Admin')}
                        </AddMoreMenuItem>
                        {!isProductMode && (
                            <AddMoreMenuItem
                                onClick={() => {
                                    if (isProductMode) return;
                                    setDropdownClose();
                                    onOpenCatalog?.();
                                }}
                            >
                                <Icon>
                                    <ThGridIcon />
                                </Icon>
                                {stringFormat(T._('From {0} product', 'Admin'), 'Zakeke')}
                            </AddMoreMenuItem>
                        )}
                    </AddMoreMenu>
                </AddMoreDropdown>
            )}
        </>
    );
};

export default BottomPanel;
